import { useState } from "react";
import FetcherApi from "components/components/FetcherApi";
import urlTools from "components/tools/urlTools";
import SmartIdErrorMessagesService from "components/services/SmartIdErrorMessagesService";
import { smartIdApi } from "../../../../utils/smartIdApi.js";
import { SMART_ID_POLLING_RESULT } from "./smartIdConstants.js";

export function useSmartIdSessionPolling() {
  const [pin, setPin] = useState("");
  const [sessionPollingError, setSessionPollingError] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const startPersonalCodeSession = async (validPersonalCode, legalEntityId) => {
    setIsAuthenticating(true);
    const result = await smartIdApi.startPersonalCodeSession(validPersonalCode, legalEntityId);

    if (!result.success) {
      setSessionPollingError(result.error);
      setIsAuthenticating(false);
      return { success: false, error: result.error };
    } else {
      setPin(result.data);
      return { success: true };
    }
  };

  const startUserIdSession = async () => {
    setIsAuthenticating(true);
    const result = await smartIdApi.startUserIdSession();
    console.debug("User ID session result:", result);
    if (!result.success) {
      setSessionPollingError(result.error);
      setIsAuthenticating(false);
      return false;
    }

    setPin(result.data);
    return true;
  };

  const pollUserIdSession = async () => {
    return pollSession(urlTools.constructRoute("/api/smart-id-auth/session-poll/user-id"));
  };

  const pollPersonalCodeSession = async () => {
    return pollSession(urlTools.constructRoute("/api/smart-id-login/session-poll/personal-code"));
  };
  
  const pollSession = async (url) => {
    const httpResponse = await FetcherApi.post(url);
    const result = httpResponse.response.data;
    
    if (!result.success) {
      if (result.error === "smart_id_session_expired") {
        setSessionPollingError(SmartIdErrorMessagesService.getErrorMessage(result.error));
        setIsAuthenticating(false);
        return SMART_ID_POLLING_RESULT.EXPIRED;
      }
      if (result.error) {
        const errorMessage = SmartIdErrorMessagesService.getErrorMessage(result.error);
        setSessionPollingError(errorMessage);
        setIsAuthenticating(false);
        return SMART_ID_POLLING_RESULT.ERROR;
      }
      return SMART_ID_POLLING_RESULT.CONTINUE; // no error message - continue polling
    } else {
      return SMART_ID_POLLING_RESULT.SUCCESS;
    }
  };

  const startHybridSession = async (validPersonalCode) => {
    const result = await smartIdApi.startHybridSession(validPersonalCode);
    
    if (!result.success) {
      return { success: false, error: result.error };
    } else {
      setPin(result.data);
      setIsAuthenticating(true);
      return { success: true };
    }
  };

  const pollHybridSession = async () => {
    const result = await smartIdApi.pollHybridSession();

    if (result.status === SMART_ID_POLLING_RESULT.EXPIRED || result.status === SMART_ID_POLLING_RESULT.ERROR) {
      setSessionPollingError(result.error);
      setIsAuthenticating(false);
      return result.status;
    }
    
    if (result.status === SMART_ID_POLLING_RESULT.CONTINUE) {
      return SMART_ID_POLLING_RESULT.CONTINUE;
    }
    
    setIsAuthenticating(false);
    return SMART_ID_POLLING_RESULT.SUCCESS;
  };


  const removeSmartId = async () => {
    const smartIdRemoveResponse = await FetcherApi.post(urlTools.constructRoute("/api/smart-id/remove"));
    const removeResult = smartIdRemoveResponse.response.data;
    
    if (!removeResult.success) {
      const errorMessage = SmartIdErrorMessagesService.getErrorMessage(removeResult.error);
      setSessionPollingError(errorMessage);
      setIsAuthenticating(false);
      return false;
    }
    
    setIsAuthenticating(false);
    return true;
  };

  const addSmartIdToAccount = async () => {
      const isPersonalCodeRegisteredResult = await smartIdApi.isPersonalCodeRegistered();
      if (isPersonalCodeRegisteredResult.success && isPersonalCodeRegisteredResult.data === true) {
      setSessionPollingError("personal_code_duplicate");
      setIsAuthenticating(false);
      return { status: SMART_ID_POLLING_RESULT.ERROR, error: "personal_code_duplicate" };
    }

    const result = await smartIdApi.addSmartIdToAccount();
    
    if (result.status === SMART_ID_POLLING_RESULT.EXPIRED || result.status === SMART_ID_POLLING_RESULT.ERROR) {
      setSessionPollingError(result.error);
      setIsAuthenticating(false);
      return { status: result.status };
    }
    
    if (result.status === SMART_ID_POLLING_RESULT.CONTINUE) {
      return { status: SMART_ID_POLLING_RESULT.CONTINUE };
    }

    setIsAuthenticating(false);
    return { status: SMART_ID_POLLING_RESULT.SUCCESS, personalInfo: result.personalInfo };
  };

  const resetError = () => {
    setSessionPollingError("");
  };

  return {
    pin,
    sessionPollingError,
    isAuthenticating,
    startUserIdSession,
    startPersonalCodeSession,
    pollUserIdSession,
    pollPersonalCodeSession,
    startHybridSession,
    pollHybridSession,
    addSmartIdToAccount,
    removeSmartId,
    setSessionPollingError,
    resetError  
  };
} 